import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import intl from 'react-intl-universal';
import SweetAlert from 'react-bootstrap-sweetalert';
import PdfFireViewer from '../PdfFireViewer';
import { downloadAttachment, fnGetImageAttachment } from '../../actions/ActionDocumentDetails';
import cImageDownload from '../../pages/document/img/icon-download.svg';

const QualityPanelViewer = ({ evidence, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentData, setDocumentData] = useState(null);
  const [previewUnavailable, setPreviewUnavailable] = useState(null);

  const isBase64 = (str) => {
    try {
      return typeof str === 'string' &&
        str.length % 4 === 0 &&
        /^[A-Za-z0-9+/]+={0,2}$/.test(str);
    } catch {
      return false;
    }
  };

  const resolveDocumentSource = (response) => {
    if (isBase64(response.cContent)) {
      return `data:application/pdf;base64,${response.cContent}`;
    }
    return response.cContent;
  };

  const loadDocument = async () => {
    try {
      setLoading(true);
      setError(null);
      setPreviewUnavailable(null);

      if (!evidence) throw new Error('Evidência não fornecida para o visualizador');

      const fileName = evidence.evid_ctitle || '';
      const fileExtension = fileName.split('.').pop().toLowerCase();
      const bIsLocal = evidence.evid_cattachment?.startsWith("customer\\") || false;

      const response = await fnGetImageAttachment(
        evidence.evid_nid,
        evidence.evid_ctitle,
        bIsLocal,
        evidence.evid_cmodulo || 'evidence'
      );

      if (response?.cType === 'unavailable') {
        setPreviewUnavailable(response);
        setDocumentData('');
        return;
      }

      const isImage = ['jpg', 'jpeg', 'png', 'bmp', 'gif'].includes(fileExtension);
      if (isImage) {
        setDocumentData(response.cContent);
        return;
      }

      if (response.cType === 'base64') {
        setDocumentData(`data:application/pdf;base64,${response.cContent}`);
        return;
      }

      if (response.cType === 'pdf') {
        setDocumentData(`data:application/pdf;base64,${response.cContent}`);
        return;
      }

      setDocumentData(resolveDocumentSource(response));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const run = async () => {
      try {
        await loadDocument();
      } catch (err) {
        // keep hook stable; errors are set in loadDocument
      }
    };

    run();
  }, [evidence]);

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (evidence?.evid_nid) {
        await downloadAttachment(evidence.evid_nid, evidence.evid_ctitle, '', evidence.evid_cmodulo || 'evidence');

      }
    } catch (err) {
      console.error('Erro ao baixar documento:', err);
      setError(err);
    }
  };

  if (!evidence) return null;

  return (
    <SweetAlert
      customClass="modal-edit modalMedium scrollBar"
      title=""
      onConfirm={onClose}
      showConfirm={false}
      closeOnClickOutside
    >
      <div className="head-modal">
        {intl.get("DocumentEditor.view_attachment")}
        <span
          role="button"
          tabIndex="0"
          aria-labelledby="head-modal"
          onClick={handleDownload}
          className="attachment-download"
        ><img src={cImageDownload} /></span>
        <span
          role="button"
          tabIndex="0"
          aria-labelledby="head-modal"
          onClick={onClose}
          className="close"
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {error ? (
          <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
            {error.message || intl.get('ViewPDF.error_loading_document')}
          </div>
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            {intl.get('ViewPDF.loading')}...
          </div>
        ) : (
          <>
            {['jpg', 'jpeg', 'png', 'bmp', 'gif'].some(ext => evidence.evid_ctitle?.toLowerCase().endsWith(ext)) ? (
              <img
                src={documentData}
                alt={evidence.evid_ctitle}
                style={{ maxWidth: '100%', maxHeight: '80vh', margin: '0 auto' }}
              />
            ) : (
              <PdfFireViewer
                pdfBlob={previewUnavailable ? '' : documentData}
                canDownload={true}
                nameFile={evidence.evid_ctitle || 'documento.pdf'}
                downloadUrl={previewUnavailable?.cOriginalUrl || ''}
                downloadFileName={previewUnavailable?.cOriginalFileName || evidence.evid_ctitle || 'documento.pdf'}
                fallbackReason={previewUnavailable?.cReason || null}
                showRetry={Boolean(previewUnavailable)}
                onRetry={loadDocument}
              />
            )}
          </>
        )}
      </div>
    </SweetAlert>
  );
};

QualityPanelViewer.propTypes = {
  evidence: PropTypes.shape({
    evid_nid: PropTypes.number,
    evid_cattachment: PropTypes.string,
    evid_ctitle: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired
};

QualityPanelViewer.defaultProps = {
  evidence: null
};

export default QualityPanelViewer;
